import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { FormFieldApi } from '../types';

export interface DatePickerProps {
  field: FormFieldApi<string>;
  name: string;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  sx?: any;
}

/**
 * Detect user's preferred date format based on browser locale
 */
const getUserDateFormat = (): 'DD-MM-YYYY' | 'MM-DD-YYYY' => {
  try {
    // Get browser locale
    const locale = navigator.language || 'en-GB';

    // Test date: January 2, 2000 (to clearly distinguish MM vs DD)
    const testDate = new Date(2000, 0, 2); // Month is 0-indexed
    const formatted = testDate.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Check if the format starts with 01 (month) or 02 (day)
    if (formatted.startsWith('01')) {
      return 'MM-DD-YYYY'; // US format
    } else {
      return 'DD-MM-YYYY'; // Most other locales
    }
  } catch {
    // Default to DD-MM-YYYY if detection fails
    return 'DD-MM-YYYY';
  }
};

const formatDate = (date: Dayjs | Date | string) => {
  const format = 'YYYY-MM-DD'
  if (dayjs.isDayjs(date)) {
    return date.format(format);
  }
  return dayjs(date).format(format);
}

/**
 * DatePicker component that integrates with TanStack Form
 * Uses MUI X DatePicker component with locale-aware formatting
 */
export const DatePicker = ({
  field,
  name,
  label,
  helperText,
  disabled,
  readOnly,
  required,
  sx,
}: DatePickerProps) => {
  const { state, getMeta } = field;
  const meta = getMeta();
  const error = meta.touchedErrors.length > 0;
  const errorMessage = meta.touchedErrors[0] as string | undefined;

  // Get user's preferred date format
  const userFormat = getUserDateFormat();
  const displayFormat = userFormat; // DD-MM-YYYY or MM-DD-YYYY

  const handleDateChange = (value: Dayjs | Date | null) => {
    if (value) {
      const dayjsValue = dayjs.isDayjs(value) ? value : dayjs(value);
      if (dayjsValue.isValid()) {
        field.handleChange(formatDate(dayjsValue));
      } else {
        field.handleChange('');
      }
    } else {
      field.handleChange('');
    }
  };

  const dateValue = state.value && dayjs(state.value).isValid() ? dayjs(state.value) : null;

  // Construct helper text with format hint
  const formatHint = `Format: ${userFormat}`;
  const displayHelperText = error
    ? errorMessage
    : helperText
    ? `${helperText} (${formatHint})`
    : formatHint;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiDatePicker
        label={label}
        format={displayFormat}
        value={dateValue}
        onChange={handleDateChange}
        onClose={() => field.handleBlur()}
        slotProps={{
          textField: {
            name,
            error,
            helperText: displayHelperText,
            fullWidth: true,
            required,
            onBlur: () => field.handleBlur(),
            placeholder: userFormat,
          },
        }}
        disabled={disabled}
        readOnly={readOnly}
        sx={sx}
      />
    </LocalizationProvider>
  );
}; 